import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader2, Download, Target, TrendingUp, Activity, ExternalLink, ChevronDown, ChevronUp, ArrowUpDown, Upload } from 'lucide-react';
import Papa from 'papaparse';
import { API_URL } from './constants';
import { useLocation } from 'react-router-dom';

const ModelEvaluationUI = ({ user }) => {

  const location = useLocation();
  const { userDetails, dataToEvaluate, targetColumns } = location.state || {};

  // console.log("Data from navigate:", rowData);
  // console.log("Columns from navigate:", targetColumns);

  // alert("Data received:", dataToEvaluate);
  // alert("Columns received:", targetColumns);

  const [models, setModels] = useState([]);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [resultFiles, setResultFiles] = useState({ csvPath: null, metricsPath: null });
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('accuracy');
  const [sortDirection, setSortDirection] = useState('desc');
  // const [availableTargets, setAvailableTargets] = useState([]);
  const [targetColumn, setTargetColumn] = useState("");
  const [similarityThreshold, setSimilarityThreshold] = useState(0.8);

  // Stats for custom file upload
  const [customCsvData, setCustomCsvData] = useState(null);
  const [customColumns, setCustomColumns] = useState(null);

  const activeCsvContent = customCsvData || dataToEvaluate;
  const activeColumns = customColumns || targetColumns || [];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 50 * 1024) {
        setError("File size exceeds 50KB limit.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        // Parse headers and data to populate dropdown and state
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              setCustomCsvData(results.data);
              const headers = results.meta.fields || Object.keys(results.data[0]);
              setCustomColumns(headers);
              // Successfully loaded new columns, set the first one as default
              if (headers.length > 0) {
                setTargetColumn(headers[0]);
              }
            }
          },
          error: (err) => {
            setError("Failed to parse CSV file: " + err.message);
          }
        });
      };
      reader.readAsText(file);
    }
  };

  const availableTargets = activeColumns;
  const info = user.accessToken;

  useEffect(() => {
    console.log("Target Columns received:", targetColumns); // Debug
    if (targetColumns && targetColumns.length > 0) {
      setTargetColumn(targetColumns[0]);
    }
  }, [targetColumns]);

  const startEvaluation = async () => {
    setIsEvaluating(true);
    setModels([]);
    setResultFiles({ csvPath: null, metricsPath: null });
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/benchmarking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${info}`
        },
        body: JSON.stringify({
          file_path: 'data.csv',
          csvContent: activeCsvContent,
          target_column: targetColumn,
          threshold: similarityThreshold
        })
      });

      if (!response.ok) throw new Error('Failed to start evaluation');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          try {
            // Handle SSE format (data: {...})
            const jsonStr = line.startsWith('data: ') ? line.slice(6) : line;
            const update = JSON.parse(jsonStr);

            if (update.status === 'processing') {
              setModels(prev => {
                const exists = prev.find(m => m.name === update.model_name);
                if (exists) return prev;
                return [...prev, {
                  id: update.model_name,
                  name: update.model_name,
                  status: 'processing',
                  progress: update.progress,
                  message: update.message,
                  accuracy: null,
                  recall: null,
                  precision: null,
                  f1Score: null
                }];
              });
            }
            else if (update.status === 'model_complete') {

              setModels(prevModels => [...prevModels, {
                name: update.model_name,
                status: 'complete',
                metrics: update.metrics,
                message: update.message,
                accuracy: update.metrics ? (update.metrics.accuracy * 100).toFixed(1) : null,
                recall: update.metrics ? (update.metrics.recall * 100).toFixed(1) : null,
                precision: update.metrics ? (update.metrics.precision * 100).toFixed(1) : null,
                f1Score: update.metrics ? (update.metrics.f1_score * 100).toFixed(1) : null,
                resultsUrl: '#' // Could be populated from backend if available
              }]);
            }
            else if (update.status === 'model_error') {
              setModels(prev => prev.map(m =>
                m.name === update.model_name
                  ? { ...m, status: 'error', error: update.error, message: update.message }
                  : m
              ));
            }
            else if (update.status === 'all_complete') {
              setResultFiles({
                csvPath: update.output_path,
                metricsPath: update.metrics_path
              });
              setIsEvaluating(false);
            }
          } catch (e) {
            setIsEvaluating(false);
            console.error('Error parsing update:', e);
          }
        }
      }
    } catch (err) {
      setError(err.message);
      setIsEvaluating(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown size={14} className="text-blue-400" />;
    }
    return sortDirection === 'asc'
      ? <ChevronUp size={14} className="text-blue-600" />
      : <ChevronDown size={14} className="text-blue-600" />;
  };

  const sortedModels = [...models].sort((a, b) => {
    if (sortField === 'name') {
      const aVal = a.name;
      const bVal = b.name;
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    } else {
      const aVal = parseFloat(a[sortField]) || -1;
      const bVal = parseFloat(b[sortField]) || -1;
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
  });

  const getAccuracyColor = (accuracy) => {
    if (!accuracy) return 'text-gray-400';
    const val = parseFloat(accuracy);
    if (val >= 90) return 'text-green-600';
    if (val >= 75) return 'text-blue-600';
    if (val >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRecallColor = (recall) => {
    if (!recall) return 'text-gray-400';
    const val = parseFloat(recall);
    if (val >= 90) return 'text-green-600';
    if (val >= 75) return 'text-blue-600';
    if (val >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const handleDownload = async (path) => {
    if (!path) return;

    try {
      // Extract filename in case path is a full URL or file path
      const filename = path.split(/[/\\]/).pop();

      const response = await fetch(`${API_URL}/api/download-benchmarking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${info}`
        },
        body: JSON.stringify({ filename })
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download file');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-blue-600 mb-1">
              Model Performance Dashboard
            </h1>
            <p className="text-gray-600 text-sm">Track and compare your machine learning models in real-time</p>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* File Upload */}
            <div className="mr-2">
              <input
                type="file"
                accept=".csv"
                id="csv-upload"
                className="hidden"
                onChange={handleFileUpload}
                disabled={isEvaluating}
              />
              <label
                htmlFor="csv-upload"
                className={`flex items-center gap-2 px-4 py-2 bg-white border border-blue-200 rounded-lg text-sm text-blue-600 font-medium cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm ${isEvaluating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Upload size={16} />
                Upload CSV
              </label>
            </div>
            {/* Similarity Slider */}
            <div className="flex flex-col justify-center mr-6">
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="similarity-slider" className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Cosine Similarity
                </label>
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 min-w-[3rem] text-center">
                  {similarityThreshold}
                </span>
              </div>
              <input
                id="similarity-slider"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={similarityThreshold}
                onChange={(e) => setSimilarityThreshold(parseFloat(e.target.value))}
                disabled={isEvaluating}
                className="w-48 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600 hover:accent-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              />
            </div>

            <div className="flex items-center gap-2">
              <Target className="text-blue-600" size={18} />
              <select
                id="target-column"
                value={targetColumn}
                onChange={(e) => setTargetColumn(e.target.value)}
                disabled={isEvaluating}
                className="px-4 py-2.5 pr-10 bg-white border border-blue-200 rounded-lg text-sm text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer hover:border-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px] appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%232563eb%22%20d%3D%22M6%209L1%204h10z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_0.75rem_center] bg-[length:12px] shadow-sm hover:shadow-md"
              >
                {availableTargets.map(target => (
                  <option key={target} value={target}>
                    {target}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={startEvaluation}
              disabled={isEvaluating}
              className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md whitespace-nowrap ${isEvaluating
                ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
                }`}
            >
              {isEvaluating ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Evaluating...
                </span>
              ) : (
                'Start Evaluation'
              )}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-600">Error: {error}</p>
          </div>
        )}

        {/* Progress Display Modal */}
        {isEvaluating && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm transition-all">
            <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-2xl max-w-md w-full mx-4 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg ring-1 ring-blue-100">
                    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Evaluating Models</h3>
                    <p className="text-sm text-gray-500">Analyzing metrics & performance...</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-500 px-1">
                  <span>Processing data</span>
                  <span>Please wait...</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden ring-1 ring-gray-200/50">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"
                    style={{
                      width: '40%',
                      animation: 'progress 2s infinite ease-in-out'
                    }}
                  />
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center">
                <p className="text-xs text-blue-600/80 animate-pulse font-medium">
                  Generating comparative insights
                </p>
              </div>
            </div>

            <style>{`
              @keyframes progress {
                0% { margin-left: -40%; width: 40%; }
                50% { margin-left: 30%; width: 40%; }
                100% { margin-left: 100%; width: 40%; }
              }
            `}</style>
          </div>
        )}

        {/* Download Section - Shows when files are available */}
        {(resultFiles.csvPath || resultFiles.metricsPath) && (
          <div className="mb-6 bg-white rounded-lg shadow-md border border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-gray-900">Results Available</span>
              </div>
              <div className="flex items-center gap-3">
                {resultFiles.csvPath && (
                  <button
                    onClick={() => handleDownload(resultFiles.csvPath)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
                  >
                    <Download size={16} />
                    CSV Report
                  </button>
                )}
                {resultFiles.metricsPath && (
                  <button
                    onClick={() => handleDownload(resultFiles.metricsPath)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
                  >
                    <Download size={16} />
                    Metrics JSON
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-blue-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-blue-50 border-b border-blue-200">
                  <th
                    className="px-6 py-4 text-left text-xs font-semibold text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-blue-100 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Model Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-semibold text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-blue-100 transition-colors"
                    onClick={() => handleSort('accuracy')}
                  >
                    <div className="flex items-center gap-2">
                      <TrendingUp size={14} className="text-blue-600" />
                      Accuracy
                      {getSortIcon('accuracy')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-semibold text-blue-600 uppercase tracking-wider cursor-pointer hover:bg-blue-100 transition-colors"
                    onClick={() => handleSort('recall')}
                  >
                    <div className="flex items-center gap-2">
                      <Activity size={14} className="text-blue-600" />
                      Recall
                      {getSortIcon('recall')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-blue-600 uppercase tracking-wider">
                    Results
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                {models.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                          <Target className="text-blue-600" size={24} />
                        </div>
                        <p className="text-gray-500">
                          Please wait while your dataset is being evaluated...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  models.map((model, idx) => (
                    <tr
                      key={model.id}
                      className="hover:bg-blue-50/50 transition-all duration-200 group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(model.status)}
                          <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                            <span className="text-blue-600 font-medium text-xs">{idx + 1}</span>
                          </div>
                          <div className="text-sm font-medium text-gray-800">{model.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {model.accuracy ? (
                          <div className={`text-base font-semibold ${getAccuracyColor(model.accuracy)}`}>
                            {model.accuracy}%
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {model.status === 'processing' && (
                              <>
                                <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                                <span className="text-sm text-gray-400">Calculating...</span>
                              </>
                            )}
                            {model.status === 'error' && (
                              <span className="text-sm text-red-500">Failed</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {model.recall ? (
                          <div className={`text-base font-semibold ${getRecallColor(model.recall)}`}>
                            {model.recall}%
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {model.status === 'processing' && (
                              <>
                                <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                                <span className="text-sm text-gray-400">Calculating...</span>
                              </>
                            )}
                            {model.status === 'error' && (
                              <span className="text-sm text-red-500">Failed</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {model.status === 'complete' ? (
                          <button
                            onClick={() => {
                              // Show detailed metrics modal or expand row
                              alert(`F1 Score: ${model.f1Score}%\nPrecision: ${model.precision}%\nCorrect: ${model.metrics?.correct_predictions}/${model.metrics?.total_predictions}`);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
                          >
                            View Details
                            <ExternalLink size={16} />
                          </button>
                        ) : model.status === 'processing' ? (
                          <span className="text-xs text-gray-500">{model.progress}</span>
                        ) : model.status === 'error' ? (
                          <span className="text-xs text-red-500">{model.message}</span>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModelEvaluationUI;